import { create } from "zustand";
import type { Video } from "@/machines/types/video.ts";

/**
 * Video rows kept per camera before the oldest (by `start_time`) are dropped.
 * The availability strip fetches a wide window with `limit: 1000`; the tile only
 * ever needs the clips around the cursor, so an unbounded list would just hold
 * detections payloads alive for a whole review session.
 */
const MAX_ROWS_PER_DEVICE = 500;

/**
 * How long a fetched row may be served from here. `Video.signedUrl` carries no
 * expiry field and nothing in this app re-signs it — and unlike `<img>` (see
 * `ml-exports/hooks/useSignedImage.ts`) the `<video>` element has no error
 * handler, so a dead URL presents as a silently stalled tile. Rows past this age
 * are dropped so a long session can never hand out a stale URL.
 */
const MAX_ROW_AGE_MS = 30 * 60 * 1000;

interface VideoRecord {
  video: Video;
  /** `video.start_time` as epoch ms (parsed once on write). */
  startMs: number;
  /** `video.end_time` as epoch ms (parsed once on write). */
  endMs: number;
  /** When this row arrived from the API — the staleness key for the signed URL. */
  fetchedAt: number;
  /**
   * When this row was FIRST seen, preserved across re-fetches (`fetchedAt` moves
   * every time the signed URL is refreshed). Together with `startMs` this is how
   * long the camera's pipeline took to make the clip fetchable, which is what
   * sets the live playback buffer.
   */
  firstSeenAt: number;
}

interface VideoLibraryState {
  /** Per camera, ascending by `startMs`. */
  devices: Record<string, VideoRecord[]>;
  /**
   * When this session first pulled rows for a camera. Clips that STARTED before
   * that were already history when we saw them, so their "first seen" says
   * nothing about the pipeline — see {@link VideoLibraryState.getClipArrivals}.
   */
  watchingSince: Record<string, number>;
  /** Merge an API batch in: dedupes by video id, keeps sorted, prunes stale/excess rows. */
  addVideos: (deviceMxid: string, videos: Video[]) => void;
  /**
   * Fresh rows covering the instant `time` — or, with `windowMs`, overlapping
   * `[time - windowMs, time + windowMs]`. Ascending by start, never stale.
   */
  getVideosAt: (deviceMxid: string, time: number, windowMs?: number) => Video[];
  /** Whether a fresh row already covers `time` — i.e. no request is needed for it. */
  hasVideoAt: (deviceMxid: string, time: number) => boolean;
  /**
   * Clips that arrived WHILE WATCHING, oldest first, as (clip start, first seen)
   * pairs — the input to the live playback buffer.
   *
   * Excludes anything that started before this session began watching the
   * camera: the first response carries the whole window at once, and counting
   * that backlog as "arrivals" would read as a pipeline minutes behind and park
   * playback needlessly far back at every session start.
   */
  getClipArrivals: (deviceMxid: string) => { startMs: number; visibleAtMs: number }[];
}

/**
 * In-memory pool of `/videos/around` rows, shared by everything that fetches
 * them for the same camera.
 *
 * The timeline's availability strip (`MapTimelineRow`) already pulls a WIDER
 * window (±1050s, limit 1000) than the video tile needs (±300s, limit 20), from
 * the same endpoint, returning the same rows. Producers push their responses in
 * here; the tile reads them back instead of issuing its own request.
 *
 * NOT persisted, deliberately: this holds whole rows including `signedUrl`,
 * which expires with nothing to re-sign it. Rows are session-scoped exactly like
 * the React Query cache, and additionally age out after {@link MAX_ROW_AGE_MS}.
 * The persisted `shared/stores/useVideoCoverageStore.ts` is a different job — it
 * stores timestamps only, to suppress re-probing known-empty ranges.
 */
export const useVideoLibraryStore = create<VideoLibraryState>()((set, get) => ({
  devices: {},
  watchingSince: {},

  addVideos: (deviceMxid, videos) => {
    if (!deviceMxid || videos.length === 0) return;
    set((state) => {
      const now = Date.now();
      const cutoff = now - MAX_ROW_AGE_MS;
      const existing = state.devices[deviceMxid] ?? [];

      const byId = new Map<number, VideoRecord>();
      for (const record of existing) {
        if (record.fetchedAt >= cutoff) byId.set(record.video.id, record);
      }
      let changed = byId.size !== existing.length;

      for (const video of videos) {
        const previous = byId.get(video.id);
        // Same row object as the one already held (React Query hands back the
        // cached array on re-render): nothing to refresh, and re-setting it would
        // notify every subscriber for no reason.
        if (previous?.video === video) continue;
        const startMs = new Date(video.start_time).getTime();
        const endMs = new Date(video.end_time).getTime();
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
        byId.set(video.id, {
          video,
          startMs,
          endMs,
          fetchedAt: now,
          // Kept from the row we already held: a re-fetch refreshes the URL, it
          // does not mean the clip arrived again.
          firstSeenAt: previous?.firstSeenAt ?? now,
        });
        changed = true;
      }
      if (!changed) return state;

      const merged = [...byId.values()]
        .sort((a, b) => a.startMs - b.startMs)
        // Sorted ascending, so the tail is the newest footage — drop the oldest.
        .slice(-MAX_ROWS_PER_DEVICE);

      return {
        devices: { ...state.devices, [deviceMxid]: merged },
        watchingSince: state.watchingSince[deviceMxid]
          ? state.watchingSince
          : { ...state.watchingSince, [deviceMxid]: now },
      };
    });
  },

  getClipArrivals: (deviceMxid) => {
    const records = get().devices[deviceMxid];
    const since = get().watchingSince[deviceMxid];
    if (!records?.length || since == null) return [];
    return records
      .filter((record) => record.startMs >= since)
      .map((record) => ({ startMs: record.startMs, visibleAtMs: record.firstSeenAt }));
  },

  getVideosAt: (deviceMxid, time, windowMs = 0) => {
    const records = get().devices[deviceMxid];
    if (!records?.length) return [];
    const cutoff = Date.now() - MAX_ROW_AGE_MS;
    const from = time - windowMs;
    const to = time + windowMs;
    return records
      .filter((record) => record.fetchedAt >= cutoff && record.startMs <= to && record.endMs >= from)
      .map((record) => record.video);
  },

  hasVideoAt: (deviceMxid, time) => {
    const records = get().devices[deviceMxid];
    if (!records?.length) return false;
    const cutoff = Date.now() - MAX_ROW_AGE_MS;
    return records.some(
      (record) => record.fetchedAt >= cutoff && record.startMs <= time && record.endMs >= time,
    );
  },
}));
